import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import {
  applyCartEditButtonCopy,
  applyCartEditQuantity,
  buildProductPageUrl,
  cancelProductContentRequest,
  loadProductContent,
  renderProductContent,
} from '@theme/product-options-loader';

/** @typedef {{
 *   productUrl: string,
 *   productTitle?: string,
 *   variantId?: string | null,
 *   cartLineKey?: string,
 *   cartQuantity?: number | string,
 * }} QuickAddOpenOptions */

const CART_EDIT_DATA_ATTRIBUTES = ['cartLineKey', 'cartVariantId', 'cartQuantity', 'cartProductUrl'];
const QUICK_ADD_REQUEST_KEY = 'quick-add';
const QUICK_ADD_PREFETCH_REQUEST_KEY = 'quick-add-prefetch';

/** Only the latest Quick Add activation is allowed to update the shared shell. */
let quickAddRequestId = 0;

/**
 * @param {QuickAddDialog} dialog
 * @param {QuickAddOpenOptions} options
 */
function setCartEditContext(dialog, options) {
  if (options.cartLineKey) {
    dialog.dataset.cartLineKey = options.cartLineKey;
    dialog.dataset.cartVariantId = options.variantId ?? '';
    dialog.dataset.cartQuantity = String(options.cartQuantity ?? 1);
    dialog.dataset.cartProductUrl = options.productUrl ?? '';
    return;
  }

  clearCartEditContext(dialog);
}

/**
 * @param {QuickAddDialog} dialog
 */
function clearCartEditContext(dialog) {
  for (const attribute of CART_EDIT_DATA_ATTRIBUTES) {
    delete dialog.dataset[attribute];
  }
}

/**
 * @param {string} productPageUrl
 */
function parkViewDetailsLink() {
  const link = document.querySelector('.quick-add-modal__view-details');
  const dialogComponent = document.getElementById('quick-add-dialog');

  if (!(link instanceof HTMLAnchorElement) || !dialogComponent) return;

  link.hidden = true;
  dialogComponent.querySelector('dialog')?.appendChild(link);
}

/**
 * @param {Element} modalContent
 * @param {string} productPageUrl
 */
function placeViewDetailsLink(modalContent, productPageUrl) {
  const link = document.querySelector('.quick-add-modal__view-details');
  if (!(link instanceof HTMLAnchorElement)) return;

  const buttons = modalContent.querySelector('.product-form-buttons');
  if (!buttons) return;

  link.href = productPageUrl;
  link.hidden = false;
  buttons.appendChild(link);
}

/**
 * @param {Element} productGrid
 * @param {QuickAddOpenOptions} options
 * @returns {boolean} Whether product content was rendered into the modal.
 */
function updateQuickAddModal(productGrid, options) {
  const modalContent = document.getElementById('quick-add-modal-content');
  const productPageUrl = buildProductPageUrl(options.productUrl, options.variantId);

  if (!productGrid || !modalContent) return false;

  // The link lives inside the content root, so it has to be moved out of the
  // way before the morph replaces everything under it.
  parkViewDetailsLink();

  renderProductContent(modalContent, productGrid, {
    variantId: options.variantId,
    compact: isMobileBreakpoint(),
    productTitle: options.productTitle ?? '',
    productPageUrl,
  });

  placeViewDetailsLink(modalContent, productPageUrl);

  if (options.cartLineKey) {
    applyCartEditButtonCopy(modalContent);
    applyCartEditQuantity(modalContent, options.cartQuantity);
  }

  return true;
}

/**
 * @param {QuickAddOpenOptions} options
 * @returns {Promise<boolean>} Whether the requested product reached the ready state.
 */
async function openQuickAddModal(options) {
  const dialogComponent = document.getElementById('quick-add-dialog');
  if (!(dialogComponent instanceof QuickAddDialog)) return false;

  const requestId = ++quickAddRequestId;
  dialogComponent.showLoadingState(options);

  try {
    const productPageUrl = buildProductPageUrl(options.productUrl, options.variantId);
    const productGrid = await loadProductContent(productPageUrl, { requestKey: QUICK_ADD_REQUEST_KEY });

    // A newer click or a deliberate close owns the shell now.
    if (requestId !== quickAddRequestId) return false;

    if (!productGrid) {
      throw new Error('Product option content was unavailable');
    }

    // The form reads this context as it connects during the morph.
    setCartEditContext(dialogComponent, { ...options, productUrl: productPageUrl });

    if (!updateQuickAddModal(productGrid, { ...options, productUrl: productPageUrl })) {
      throw new Error('Quick Add modal content could not be rendered');
    }

    dialogComponent.showReadyState();
    return true;
  } catch (error) {
    if (requestId !== quickAddRequestId) return false;

    clearCartEditContext(dialogComponent);
    dialogComponent.showErrorState();
    console.error('[QuickAdd] Failed to load product options', error);
    return false;
  }
}

function cancelQuickAddRequest() {
  quickAddRequestId += 1;
  cancelProductContentRequest(QUICK_ADD_REQUEST_KEY);
}

export class QuickAddComponent extends Component {
  /** Clicks still preparing modal content, so a double click doesn't drop the spinner early. */
  #pendingClicks = 0;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    this.#setLoading(true);

    try {
      const currentUrl = this.productPageUrl;

      await openQuickAddModal({
        productUrl: currentUrl,
        productTitle: this.dataset.productTitle ?? '',
        variantId: this.#getSelectedVariantId(),
      });

      const dialogComponent = document.getElementById('quick-add-dialog');
      if (dialogComponent instanceof QuickAddDialog) {
        this.#stayVisibleUntilDialogCloses(dialogComponent);
      }
    } catch (error) {
      const dialogComponent = document.getElementById('quick-add-dialog');

      if (dialogComponent instanceof QuickAddDialog) {
        cancelQuickAddRequest();
        dialogComponent.showLoadingState({
          productUrl: '',
          productTitle: this.dataset.productTitle ?? '',
        });
        dialogComponent.showErrorState();
        this.#stayVisibleUntilDialogCloses(dialogComponent);
      }

      console.error('[QuickAdd] Failed to resolve the selected product', error);
    } finally {
      this.#setLoading(false);
    }
  };

  /**
   * Toggles the in-button spinner on the choose button, matching the loading
   * state AddToCartComponent gives the single-variant quick add button.
   * @param {boolean} loading - Whether the modal content is being prepared.
   */
  #setLoading(loading) {
    this.#pendingClicks = Math.max(0, this.#pendingClicks + (loading ? 1 : -1));

    const button = this.querySelector('.quick-add__button--choose');

    if (!button) return;

    const isLoading = this.#pendingClicks > 0;

    button.classList.toggle('atc-loading', isLoading);

    if (isLoading) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Element | null>}
   */
  fetchProductPage(productPageUrl) {
    const resolvedUrl = buildProductPageUrl(productPageUrl, null);

    // Preloading warms the same content cache as an actual open, but owns a
    // separate request channel so moving between cards cannot cancel a click.
    return loadProductContent(resolvedUrl, { requestKey: QUICK_ADD_PREFETCH_REQUEST_KEY }).catch(() => null);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {QuickAddOpenOptions | null} */
  #lastOpenOptions = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener('click', this.#handleGalleryArrow, {
      capture: true,
      signal: this.#abortController.signal,
    });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Opens the shell immediately while the selected product is loading.
   *
   * @param {QuickAddOpenOptions} options
   */
  showLoadingState(options) {
    this.#lastOpenOptions = { ...options };
    clearCartEditContext(this);
    parkViewDetailsLink();
    this.refs.content?.replaceChildren();

    if (this.refs.dialogTitle) {
      this.refs.dialogTitle.textContent =
        options.productTitle?.trim() || this.refs.dialogTitle.dataset.defaultTitle || '';
    }

    this.#setShellState('loading');
    this.showDialog();
  }

  /** Shows the product UI after it has been rendered successfully. */
  showReadyState() {
    this.#setShellState('ready');
  }

  /** Keeps the shell open and gives the shopper an in-place retry. */
  showErrorState() {
    this.#setShellState('error');
    this.showDialog();
  }

  /** Retries the last requested product without leaving the current page. */
  retry = () => {
    if (!this.#lastOpenOptions) return;

    void openQuickAddModal(this.#lastOpenOptions);
  };

  /** @param {'loading' | 'ready' | 'error'} state */
  #setShellState(state) {
    this.dataset.state = state;
    this.refs.content?.toggleAttribute('hidden', state !== 'ready');
    this.refs.status?.toggleAttribute('hidden', state === 'ready');
    this.refs.loadingState?.toggleAttribute('hidden', state !== 'loading');
    this.refs.errorState?.toggleAttribute('hidden', state !== 'error');
  }

  /**
   * Drives the modal's own carousel from the gallery arrows.
   *
   * The arrows ship with an inline onclick that reaches for
   * `document.querySelector('slideshow-component[ref=slideshow]')`, which on a
   * product page finds the gallery behind the modal. Listening in the capture
   * phase and stopping propagation means that handler never runs, and unlike
   * stripping the attribute it survives the media gallery replacing itself on
   * every variant update.
   *
   * @param {Event} event - The click event
   */
  #handleGalleryArrow = (event) => {
    if (!(event.target instanceof Element)) return;

    const arrow = event.target.closest('.product-gallery-arrow');
    if (!arrow) return;

    event.stopPropagation();
    event.preventDefault();

    const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
      this.querySelector('slideshow-component')
    );
    if (!slideshow) return;

    if (arrow.matches('.product-gallery-arrow--previous')) {
      slideshow.previous();
    } else {
      slideshow.next();
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const footerViewDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.quick-add-modal__view-details')
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (footerViewDetailsLink) footerViewDetailsLink.href = anchorElement.href;
  };

  #applyCartEditButtonCopy = () => {
    if (!this.dataset.cartLineKey) return;

    applyCartEditButtonCopy(this);
  };

  #handleVariantUpdate = (/** @type {CustomEvent} */ event) => {
    this.#updateProductTitleLink(event);
    this.#applyCartEditButtonCopy();
  };

  #handleDialogClose = () => {
    cancelQuickAddRequest();
    clearCartEditContext(this);

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('.variant-wrapper__edit-button');
  if (!(button instanceof HTMLButtonElement)) return;

  const { productUrl, variantId, productTitle } = button.dataset;
  if (!productUrl) return;

  event.preventDefault();

  openQuickAddModal({
    productUrl,
    variantId,
    productTitle,
    cartLineKey: button.dataset.cartLineKey,
    cartQuantity: button.dataset.cartQuantity,
  });
});
